import { useState, useEffect, lazy, Suspense } from "react";
import { Helmet } from "react-helmet-async";
import { useNavigate } from "react-router-dom";
import MainHeader from "@/components/layout/MainHeader";
import HeroSection from "@/components/numerologia/HeroSection";
import ResultSection from "@/components/numerologia/ResultSection";
import { ScrollIndicator } from "@/components/numerologia/ScrollIndicator";
import { NumerologyTeasersSection } from "@/components/numerologia/NumerologyTeasersSection";
import PerformanceOptimizer from "@/components/PerformanceOptimizer";
import { calcularPiramideVida } from "@/utils/pyramidCalculation";
import { fetchExplicacoesBulk } from "@/utils/explicacoes";
import useScrollToHash from "@/hooks/useScrollToHash";

// Lazy load components
const TestimonialsSection = lazy(() => import("@/components/landing/TestimonialsSection"));
const TechnologySection = lazy(() => import("@/components/landing/TechnologySection"));
const ProcessSection = lazy(() => import("@/components/landing/ProcessSection"));
const ExclusiveBonusSection = lazy(() => import("@/components/landing/ExclusiveBonusSection"));
const PersonalUniverseSection = lazy(() => import("@/components/landing/PersonalUniverseSection"));
const FAQSection = lazy(() => import("@/components/landing/FAQSection"));
const LandingFooter = lazy(() => import("@/components/landing/LandingFooter"));

interface ResultadoPiramide {
  triangulo: string;
  numeroFinal: number;
  explicacao: string;
}

const NumerologiaDoNome = () => {
  const [resultado, setResultado] = useState<ResultadoPiramide | null>(null);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<{ nome?: string; data?: string }>({});
  const navigate = useNavigate();
  
  // Handle hash fragments in URL for navigation from blog
  useScrollToHash();
  
  // Safety fallback to ensure scroll is never locked on landing page
  useEffect(() => {
    document.body.style.overflow = "auto";
  }, []);

  // Validação de inputs
  const validarInputs = (nome: string, dataNascimento: string) => {
    const newErrors: { nome?: string; data?: string } = {};
    
    if (!nome.trim()) {
      newErrors.nome = "Nome completo é obrigatório";
    } else if (nome.trim().length < 3) {
      newErrors.nome = "Nome deve ter pelo menos 3 caracteres";
    }
    
    if (!dataNascimento) {
      newErrors.data = "Data de nascimento é obrigatória";
    } else {
      const hoje = new Date();
      const dataNasc = new Date(dataNascimento);
      if (dataNasc > hoje) {
        newErrors.data = "Data não pode ser no futuro";
      }
      if (dataNasc.getFullYear() < 1900) {
        newErrors.data = "Data muito antiga";
      }
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Calcular pirâmide
  const handleCalculate = async (nome: string, dataNascimento: string) => {
    if (!validarInputs(nome, dataNascimento)) return;
    
    setLoading(true);
    try {
      // Calcular a pirâmide da vida
      const resultadoCalculo = calcularPiramideVida(nome.trim());
      
      if (!resultadoCalculo.triangulo_invertido) {
        throw new Error("Erro no cálculo da pirâmide");
      }

      // Buscar explicação do número final
      const needed = {
        destino: new Set<number | null>(),
        missao: new Set<number | null>(),
        expressao: new Set<number | null>(),
        motivacao: new Set<number | null>(),
        impressao: new Set<number | null>(),
        ano_pessoal: new Set<number | null>(),
        psiquico: new Set<number | null>(),
        talento_oculto: new Set<number | null>(),
        harmonia_conjugal: new Set<number | null>(),
        resposta_subconsciente: new Set<number | null>(),
        tendencia_oculta: new Set<number | null>(),
        licao_carmica: new Set<number | null>(),
        divida_carmica: new Set<number | null>(),
        arcano: new Set<number | null>(),
        primeiro_ciclo_vida_e_desafio: new Set<number | null>(),
        segundo_ciclo_vida_e_desafio: new Set<number | null>(),
        terceiro_ciclo_vida_e_desafio: new Set<number | null>(),
        primeiro_momento_decisivo: new Set<number | null>(),
        segundo_momento_decisivo: new Set<number | null>(),
        terceiro_momento_decisivo: new Set<number | null>(),
        quarto_momento_decisivo: new Set<number | null>(),
        triangulo_invertido_vida: new Set([resultadoCalculo.base_triangulo_invertido]),
        triangulo_invertido_pessoal: new Set<number | null>(),
        triangulo_invertido_social: new Set<number | null>(),
        triangulo_invertido_destino: new Set<number | null>(),
        cores_expressao: new Set<number | null>()
      };
      const explicacoes = await fetchExplicacoesBulk(needed);
      
      const explicacao = explicacoes.get('triangulo_invertido_vida', resultadoCalculo.base_triangulo_invertido) || 
        "Seu número da pirâmide da vida revela aspectos importantes sobre seu caminho e propósito.";

      setResultado({
        triangulo: resultadoCalculo.triangulo_invertido,
        numeroFinal: resultadoCalculo.base_triangulo_invertido,
        explicacao
      });

      // Scroll para os resultados
      setTimeout(() => {
        document.getElementById('resultado')?.scrollIntoView({ 
          behavior: 'smooth',
          block: 'start'
        });
      }, 300);
    } catch (error) {
      console.error("Erro ao calcular pirâmide:", error);
      setErrors({ nome: "Erro no cálculo. Tente novamente." });
    } finally {
      setLoading(false);
    }
  };

  const handleViewComplete = () => {
    navigate("/carrinho-landing");
  };

  const scrollToCalculator = () => {
    document.getElementById('calculator')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <>
      <Helmet>
        <title>🔮 Descubra o Segredo Escondido no Seu Nome | Numerologia AuraCode</title>
        <meta 
          name="description" 
          content="Seu nome carrega energias que podem estar travando suas finanças, saúde e relacionamentos. Descubra se você está vibrando prosperidade ou bloqueios!" 
        />
        
        {/* Open Graph */}
        <meta property="og:title" content="🔮 Descubra o Segredo Escondido no Seu Nome" />
        <meta property="og:description" content="Seu nome pode estar bloqueando sua prosperidade. Descubra agora as energias ocultas que influenciam sua vida." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://auracode.io/numerologia-do-nome" />
        <meta property="og:image" content="https://auracode.io/og-image.jpg" />
        
        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="🔮 Descubra o Segredo Escondido no Seu Nome" />
        <meta name="twitter:description" content="Seu nome pode estar bloqueando sua prosperidade. Descubra as energias que influenciam sua vida." />
        <meta name="twitter:image" content="https://auracode.io/og-image.jpg" />
        
        {/* Schema.org structured data */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebPage",
            "name": "Numerologia do Nome - Descubra Energias Ocultas",
            "description": "Descubra se seu nome está bloqueando sua prosperidade, saúde e relacionamentos. Análise numerológica instantânea e personalizada.",
            "url": "https://auracode.io/numerologia-do-nome",
            "provider": {
              "@type": "Organization",
              "name": "AuraCode",
              "url": "https://auracode.io"
            },
            "offers": {
              "@type": "Offer",
              "name": "Análise das Energias do Nome",
              "description": "Mapa numerológico que revela bloqueios e potenciais do seu nome",
              "category": "Numerologia"
            }
          })}
        </script>
      </Helmet>
      
      <PerformanceOptimizer />
      
      <div className="min-h-screen dark-theme">
        <MainHeader />
        <ScrollIndicator />
        
        <div id="calculator">
          <HeroSection
            onCalculate={handleCalculate}
            loading={loading}
            errors={errors}
            setErrors={setErrors}
          />
        </div>
        
        <NumerologyTeasersSection onCalculate={scrollToCalculator} />
        
        {resultado && (
          <div id="resultado">
            <ResultSection
              resultado={resultado}
              onViewComplete={handleViewComplete}
            />
          </div>
        )}
        
        <Suspense fallback={<div className="py-16 text-center"><div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto" /></div>}>
          <TestimonialsSection />
        </Suspense>
        
        <Suspense fallback={<div className="py-16 text-center"><div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto" /></div>}>
          <TechnologySection />
        </Suspense>
        
        <Suspense fallback={<div className="py-16 text-center"><div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto" /></div>}>
          <ProcessSection />
        </Suspense>
        
        <Suspense fallback={<div className="py-16 text-center"><div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto" /></div>}>
          <ExclusiveBonusSection />
        </Suspense>
        
        <Suspense fallback={<div className="py-16 text-center"><div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto" /></div>}>
          <PersonalUniverseSection />
        </Suspense>
        
        <Suspense fallback={<div className="py-16 text-center"><div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto" /></div>}>
          <FAQSection />
        </Suspense>
        
        <Suspense fallback={<div className="h-20 flex items-center justify-center"><div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" /></div>}>
          <LandingFooter />
        </Suspense>
      </div>
    </>
  );
};

export default NumerologiaDoNome;